import { useState, useEffect, useCallback } from "react";
import { Link } from "react-router-dom";
import { bannerComics } from "@/mocks/comics";
import type { ComicArticle } from "@/mocks/comics";

export default function HeroSection() {
  const [current, setCurrent] = useState(0);
  const comics = bannerComics;

  const next = useCallback(() => {
    setCurrent((prev) => (prev + 1) % comics.length);
  }, [comics.length]);

  const prev = useCallback(() => {
    setCurrent((prev) => (prev - 1 + comics.length) % comics.length);
  }, [comics.length]);

  useEffect(() => {
    const timer = setInterval(next, 5000);
    return () => clearInterval(timer);
  }, [next]);

  const comic: ComicArticle = comics[current];

  return (
    <section className="relative w-full h-[420px] md:h-[560px] overflow-hidden">
      <div className="absolute inset-0">
        <img
          src={comic.coverImage}
          alt={comic.title}
          className="w-full h-full object-cover object-top"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-black/50 via-black/30 to-black/70"></div>
      </div>

      <div className="relative z-10 h-full max-w-7xl mx-auto px-4 md:px-6 flex flex-col justify-end pb-12 md:pb-16">
        <div className="flex items-center gap-3 mb-3">
          <span className="px-2.5 py-0.5 rounded-full bg-accent-500 text-background-50 text-xs font-medium whitespace-nowrap">
            {comic.category}
          </span>
          {comic.isNew && (
            <span className="px-2.5 py-0.5 rounded-full bg-primary-500 text-background-50 text-xs font-medium whitespace-nowrap">
              NEW
            </span>
          )}
          <span className="text-background-50/80 text-sm">
            <i className="ri-star-fill text-accent-500 mr-1"></i>
            {comic.rating}
          </span>
        </div>

        <h2 className="text-2xl md:text-4xl font-display text-background-50 mb-3">
          {comic.title}
        </h2>
        <p className="text-sm md:text-base text-background-50/80 max-w-xl mb-6 line-clamp-2">
          {comic.description}
        </p>

        <div className="flex items-center gap-3">
          <Link
            to={`/article/${comic.id}`}
            className="px-6 py-2.5 bg-primary-500 text-background-50 rounded-full text-sm font-medium hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer"
          >
            开始阅读
          </Link>
          <Link
            to={`/category/${comic.categorySlug}`}
            className="px-6 py-2.5 bg-background-50/20 text-background-50 rounded-full text-sm font-medium hover:bg-background-50/30 transition-colors whitespace-nowrap cursor-pointer backdrop-blur-sm"
          >
            更多{comic.category}漫画
          </Link>
        </div>

        <div className="flex items-center gap-2 mt-6">
          {comics.map((_, idx) => (
            <button
              key={idx}
              onClick={() => setCurrent(idx)}
              className={`w-2 h-2 rounded-full transition-all cursor-pointer ${
                idx === current
                  ? "bg-primary-500 w-6"
                  : "bg-background-50/40 hover:bg-background-50/60"
              }`}
              aria-label={`切换到第${idx + 1}张`}
            />
          ))}
        </div>
      </div>

      <button
        onClick={prev}
        className="absolute left-3 md:left-6 top-1/2 -translate-y-1/2 w-8 h-8 md:w-10 md:h-10 rounded-full bg-background-50/20 backdrop-blur-sm flex items-center justify-center text-background-50 hover:bg-background-50/30 transition-colors cursor-pointer"
        aria-label="上一张"
      >
        <i className="ri-arrow-left-s-line text-lg md:text-xl"></i>
      </button>
      <button
        onClick={next}
        className="absolute right-3 md:right-6 top-1/2 -translate-y-1/2 w-8 h-8 md:w-10 md:h-10 rounded-full bg-background-50/20 backdrop-blur-sm flex items-center justify-center text-background-50 hover:bg-background-50/30 transition-colors cursor-pointer"
        aria-label="下一张"
      >
        <i className="ri-arrow-right-s-line text-lg md:text-xl"></i>
      </button>
    </section>
  );
}